import { Box, Typography, Backdrop, keyframes, LinearProgress, Button } from "@mui/material";
import { alpha } from "@mui/material";
import DescriptionIcon from '@mui/icons-material/Description';
import DataObjectIcon from '@mui/icons-material/DataObject';
import TableChartIcon from '@mui/icons-material/TableChart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';

const slideIn = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
`;

const progressAnimation = keyframes`
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
`;

const checkAnimation = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

interface LoadingAnaliseAlternativoProps {
  open: boolean;
  fileName?: string;
  onCancel?: () => void;
}

export const LoadingAnaliseImportInstallments = ({ open, fileName, onCancel }: LoadingAnaliseAlternativoProps) => {
  const steps = [
    { icon: DescriptionIcon, label: "Lendo arquivo", delay: 0.7 },
    { icon: DataObjectIcon, label: "Extraindo dados", delay: 3 },
    { icon: TableChartIcon, label: "Processando linhas", delay: 6 },
    { icon: CheckCircleOutlineIcon, label: "Finalizando análise", delay: 9 },
  ];

  return (
    <Backdrop
      open={open}
      sx={{
        color: '#fff',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
        backdropFilter: 'blur(10px)',
      }}

    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          maxWidth: 400,
          width: '100%',
          px: 3,
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: 'text.primary', mb: 1, }}
          >
            Analisando seu arquivo
          </Typography>
          {fileName && (
            <Typography
              sx={{ color: 'primary.main', fontSize: '0.9rem', fontWeight: 500, }}
            >
              {fileName}
            </Typography>
          )}
        </Box>

        {/* Progress Steps */}
        <Box sx={{ width: '100%' }}>
          {steps.map((step, index) => (
            <Box
              key={step.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
                animation: `${slideIn} 0.6s ease-out forwards`,
                animationDelay: `${step.delay}s`,
                opacity: 0,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: (theme) => alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                }}
              >
                <step.icon />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.95rem',
                  }}
                >
                  {step.label}
                </Typography>
                <Box
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    overflow: 'hidden',
                    mt: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      borderRadius: 2,
                      backgroundColor: 'primary.main',
                      animation: `${progressAnimation} 1.8s ease-out forwards`,
                      animationDelay: `${step.delay + 0.5}s`,
                      width: 0,
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  animation: `${checkAnimation} 0.5s ease-out forwards`,
                  animationDelay: `${step.delay + 2}s`,
                  opacity: 0,
                  color: 'success.main',
                }}
              >
                <CheckCircleOutlineIcon fontSize="small" />
              </Box>
            </Box>
          ))}
        </Box>

        {/* Bottom Progress */}
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              },
            }}
          />
          <Typography
            sx={{
              textAlign: 'center',
              mt: 2,
              color: 'text.secondary',
              fontSize: '0.85rem',
            }}
          >
            Isso pode levar alguns segundos...
          </Typography>
        </Box>

        {/* Botão Cancelar */}
        {onCancel && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={onCancel}
            sx={{
              mt: 3,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              px: 4,
              py: 1,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.08),
              },
            }}
          >
            Cancelar Análise
          </Button>
        )}
      </Box>
    </Backdrop>
  );
};
